/**
 * Client-side mirror of backend/src/auth/access.ts - for showing and hiding UI
 * only. The API enforces every rule; these just keep buttons honest.
 */
import type { DocView, Me, Role } from "./api";

export interface Perms {
  myCodes: string[];
  rolesIn(code: string): Role[];
  has(code: string, role: Role): boolean;
  canSeePending(code: string): boolean;
  canAuthor(code: string): boolean;
  anyAuthor: boolean;
  anyApprover: boolean;
  anyAdmin: boolean;
  adminCodes: string[];
  primaryRoles: Role[];
  /** ADR-0022: TechHub rights per company, and the companies where it can be opened. */
  ch(code: string): { view: boolean; edit: boolean; sensitive: boolean; money?: boolean; files?: boolean };
  chCodes: string[];
  /** ADR-0023: ReviewHub rights per company, and the companies where it can be opened. */
  rh(code: string): { view: boolean; edit: boolean; admin: boolean };
  rhCodes: string[];
  /** ADR-0024: VendorHub rights per company (works without TechHub), and where it can be opened. */
  vh(code: string): { view: boolean; edit: boolean; admin: boolean; clients: boolean };
  vhCodes: string[];
}

export function perms(me: Me): Perms {
  const map = new Map(me.companies.map((c) => [c.code, new Set<Role>(c.roles)]));
  const any = (r: Role) => [...map.values()].some((s) => s.has(r));
  const has = (code: string, role: Role): boolean => {
    if (code === "CPY") {
      if (!map.size) return false;
      if (role === "reader") return true;
      if (role === "author") return any("admin");
      if (role === "approver") return any("approver");
      if (role === "reviewer") return any("reviewer");
      return any("admin") || me.user.is_platform_admin;
    }
    const s = map.get(code);
    if (!s) return false;
    return role === "reader" ? true : s.has(role);
  };
  const rolesIn = (code: string): Role[] =>
    code === "CPY"
      ? (["reader", "author", "reviewer", "approver", "admin"] as Role[]).filter((r) => has("CPY", r))
      : (["admin", "author", "reviewer", "approver", "reader"] as Role[]).filter((r) => map.get(code)?.has(r));
  const all = new Set<Role>(me.companies.flatMap((c) => c.roles));
  return {
    myCodes: me.companies.map((c) => c.code),
    rolesIn,
    has,
    canSeePending: (code) => has(code, "author") || has(code, "reviewer") || has(code, "approver") || has(code, "admin"),
    canAuthor: (code) => has(code, "author"),
    anyAuthor: any("author") || any("admin"),
    // Reviewers (ADR-0019) use the Approvals queue too.
    anyApprover: any("approver") || any("reviewer"),
    anyAdmin: me.can_admin.length > 0,
    adminCodes: me.can_admin.filter((c) => c !== "CPY"),
    primaryRoles: (["admin", "approver", "reviewer", "author", "reader"] as Role[]).filter((r) => all.has(r) && (r !== "reader" || all.size === 1 || [...all].every((x) => x === "reader" || x.startsWith("clienthub_") || x.startsWith("reviewhub_") || x.startsWith("vendorhub_")))),
    ch: (code) => me.companies.find((c) => c.code === code)?.clienthub ?? { view: false, edit: false, sensitive: false, money: false, files: false },
    chCodes: me.companies.filter((c) => c.clienthub?.view).map((c) => c.code),
    rh: (code) => me.companies.find((c) => c.code === code)?.reviewhub ?? { view: false, edit: false, admin: false },
    rhCodes: me.companies.filter((c) => c.reviewhub?.view).map((c) => c.code),
    vh: (code) => me.companies.find((c) => c.code === code)?.vendorhub ?? { view: false, edit: false, admin: false, clients: false },
    vhCodes: me.companies.filter((c) => c.vendorhub?.view).map((c) => c.code),
  };
}

export type PendKey = "rev" | "chg" | "draft";
export const PEND_LABEL: Record<PendKey, string> = { rev: "In review", chg: "Changes requested", draft: "Draft" };
export const pendKey = (d: DocView): PendKey | null =>
  !d.pending ? null : d.pending.state === "in_review" ? "rev" : d.pending.state === "changes_requested" ? "chg" : "draft";

export const latestDate = (d: DocView) => [d.approved_at, d.pending?.submitted_at, d.updated_at].filter(Boolean).sort().pop() ?? "";
